import { useCallback, useEffect, useState } from 'react';
import {
  useParams,
  useLocation,
  useNavigate,
  generatePath,
  useMatch,
  useOutletContext,
} from 'react-router-dom';
import { SendMessage } from 'react-use-websocket';
import {
  BlacklistDataContent,
  ButtonPseudo,
  IncomingSidepanel,
  SidePanel,
  SidePanelType,
  DocumentStatus,
  DocumentType,
  PermissionEnum,
  BlacklistUserInfo,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  changePanelState,
  clearTransactionState,
  setBlacklistData,
  setBlacklistDataVisible,
  setPanelIsOpen,
  setBlacklistDataItem,
  setBlacklistItemVisible,
} from '../store/slices/incoming-transaction';
import {
  useLazyGetTransactionAmlQuery,
  useLazyGetCustomersTransactionLogsQuery,
  useLazyGetCustomerIncomingTransactionQuery,
  useLazyGetCustomerIncomingTransactionWarningsQuery,
  useLazyGetProblemTransactionChatsQuery,
  useLazyGetProblemTransactionsDocumentsQuery,
  useLazyGetProblemTransactionDocumentByIdQuery,
  useLazyGetProblemTransactionDocumentFileQuery,
  useDeleteProblemTransactionDocumentsMutation,
  useChangeProblemTransactionDocumentCategoryMutation,
  useChangeProblemTransactionDocumentStatusMutation,
  useAddCommentToProblemTransactionDocumentMutation,
  useDeleteCommentInProblemTransactionDocumentMutation,
  useEditCommentInProblemTransactionDocumentMutation,
} from '../store/services/customers';

const ProblemIncomingTransactionHOC = ({
  matchPath,
  backPath,
  generalPath,
  amlPath,
  orderPath,
  logPath,
  warningPath,
  chatPath,
  documentPath,
  changeLogPath,
  commentsPath,
}: {
  matchPath: string;
  backPath: string;
  generalPath: string;
  amlPath: string;
  orderPath: string;
  logPath: string;
  warningPath: string;
  chatPath: string;
  documentPath: string;
  changeLogPath: string;
  commentsPath: string;
}): React.JSX.Element => {
  const {
    customersProblemTransactions: { incoming },
    customersDictionaries: { activePermissions, currenciesList },
  } = useAppSelector((state) => state);

  const [imgLink, setImgLink] = useState('');

  const { sendMessage } = useOutletContext<{
    sendMessage: SendMessage | null;
  }>();

  const { customerId, transactionId } = useParams();
  const { search } = useLocation();
  const navigate = useNavigate();
  const match = useMatch(matchPath);

  const dispatch = useAppDispatch();

  const [getIncomingAml, { isLoading: isLoadingAml }] =
    useLazyGetTransactionAmlQuery();

  const [getIncomingLogs, { isLoading: isLoadingLog }] =
    useLazyGetCustomersTransactionLogsQuery();
  const [getChats] = useLazyGetProblemTransactionChatsQuery();

  const [
    getCustomerIncomingTransactionWarningsQuery,
    { isLoading: warningsIsLoading },
  ] = useLazyGetCustomerIncomingTransactionWarningsQuery();

  const [
    getCustomerIncomingTransaction,
    { isLoading: transactionIsLoading, isFetching },
  ] = useLazyGetCustomerIncomingTransactionQuery();

  const [getTransactionDocuments] =
    useLazyGetProblemTransactionsDocumentsQuery();
  const [
    getTransactionDocumentyId,
    { isLoading: documentIsLoading, isFetching: documentIsFetching },
  ] = useLazyGetProblemTransactionDocumentByIdQuery();
  const [
    getTransactionDocumentFile,
    {
      isLoading: downloadInProgress,
      isFetching: downloadIsFetching,
      isSuccess: downloadFileIsSuccess,
    },
  ] = useLazyGetProblemTransactionDocumentFileQuery();
  const [
    deleteDocument,
    { isLoading: deleteDocumentInProgress, isSuccess: deleteDocumentIsSuccess },
  ] = useDeleteProblemTransactionDocumentsMutation();
  const [
    changeDocumentCategory,
    { isLoading: documentCategoryChangeInProgress },
  ] = useChangeProblemTransactionDocumentCategoryMutation();
  const [changeDocumentStatus, { isLoading: documentStatusChangeInProgress }] =
    useChangeProblemTransactionDocumentStatusMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCommentToProblemTransactionDocumentMutation();
  const [
    editComment,
    { isLoading: editCommentInProgress, isSuccess: editCommentIsSuccess },
  ] = useEditCommentInProblemTransactionDocumentMutation();
  const [deleteComment, { isLoading: deleteCommentInProgress }] =
    useDeleteCommentInProblemTransactionDocumentMutation();

  const getIncomingTransactionById = useCallback(() => {
    getCustomerIncomingTransaction(transactionId as string);
    getIncomingAml(transactionId as string);
    getCustomerIncomingTransactionWarningsQuery(transactionId as string);

    if (
      activePermissions?.includes(
        PermissionEnum.TRANSACTION_QUEUE_ITEMS_INCOMING_LIST,
      )
    ) {
      getIncomingLogs(transactionId as string);
    }

    if (activePermissions?.includes(PermissionEnum.CHANNELS_LIST)) {
      getChats(transactionId as string);
    }

    if (
      activePermissions?.includes(PermissionEnum.TRANSACTIONS_DOCUMENTS_LIST)
    ) {
      getTransactionDocuments({ id: transactionId as string });
    }
  }, [transactionId, activePermissions]);

  const handleClose = () => {
    dispatch(setPanelIsOpen(false));

    setTimeout(() => {
      navigate(
        generatePath(`${backPath}${search || ''}`, {
          customerId,
        }),
      );
    }, 100);
  };

  const getTransactionDocumentyIdAction = async (id: string) => {
    getTransactionDocumentyId({
      id: transactionId as string,
      documentId: id,
    });

    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    if (response && response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      setImgLink(blob);
    }
  };

  const onDeleteDocument = (id: string) => {
    deleteDocument({
      id: transactionId as string,
      body: {
        ids: [id],
      },
    });
  };

  const onChangeCustomerDocumentsStatus = (data: {
    ids: string[];
    status: DocumentStatus;
  }) => {
    changeDocumentStatus({
      id: transactionId as string,
      body: data,
    });
  };

  const onMoveDocument = (data: { ids: string[]; category: DocumentType }) => {
    changeDocumentCategory({
      id: transactionId as string,
      body: data,
    });
  };
  const onDownloadDocument = async (id: string) => {
    const response = await getTransactionDocumentFile({
      id: transactionId as string,
      documentId: id,
    });

    const currentDocument = incoming?.documents?.find((item) => item.id === id);

    if (response.data) {
      const hiddenElement = document.createElement('a');

      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);
      hiddenElement.href = blob;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${currentDocument?.name}`;
      hiddenElement.click();
    }
  };

  const onSendDocumentComment = (text: string) => {
    addComment({
      id: incoming?.document?.id as string,
      body: {
        text,
      },
    });
  };

  const onEditComment = (data: {
    id: string;
    commentId: string;
    body: {
      text: string;
    };
  }) => {
    editComment({
      id: data.id,
      commentId: data.commentId,
      body: data.body,
    });
  };

  const onDeleteComment = (data: { id: string; commentId: string }) => {
    deleteComment({
      id: data.id,
      commentId: data.commentId,
    });
  };

  useEffect(() => {
    dispatch(setPanelIsOpen(true));

    return () => {
      dispatch(clearTransactionState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (match && customerId && transactionId) {
      navigate(
        generatePath(`${generalPath}${search || ''}`, {
          customerId,
          transactionId,
        }),
      );
    }
  }, [match, customerId, transactionId]);

  const handleCloseBlacklistData = () => {
    dispatch(setBlacklistDataVisible(false));
    dispatch(setBlacklistData(undefined));
    dispatch(setBlacklistItemVisible(false));
    dispatch(setBlacklistDataItem(undefined));
    dispatch(changePanelState({ state: true, transactionId }));
  };

  useEffect(() => {
    if (customerId && !incoming?.transaction && transactionId) {
      getIncomingTransactionById();
    }
  }, [
    getIncomingTransactionById,
    customerId,
    incoming?.transaction,
    transactionId,
  ]);

  return (
    <>
      <IncomingSidepanel
        panelIsOpen={incoming.panelIsOpen}
        handleClose={handleClose}
        title="Incoming Transaction"
        tabs={{
          generalPath: generatePath(`${generalPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          orderPath: generatePath(`${orderPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          amlPath: generatePath(`${amlPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          logPath: generatePath(`${logPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          changeLogPath: generatePath(`${changeLogPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          warningPath: generatePath(`${warningPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          chatTab: generatePath(`${chatPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          documentsPath: generatePath(`${documentPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
          commentsPath: generatePath(`${commentsPath}${search || ''}`, {
            customerId,
            transactionId,
          }),
        }}
        isLoading={
          transactionIsLoading ||
          isLoadingAml ||
          isLoadingLog ||
          warningsIsLoading ||
          isFetching
        }
        warnings={incoming.warnings}
        document={incoming.document}
        documentFileIsLoading={downloadInProgress || downloadIsFetching}
        downloadInProgress={downloadInProgress || downloadIsFetching}
        imgLink={imgLink}
        onDelete={onDeleteDocument}
        deleteInProgress={deleteDocumentInProgress}
        deleteIsSuccess={deleteDocumentIsSuccess}
        onMove={onMoveDocument}
        moveInProgress={documentCategoryChangeInProgress}
        onDownload={onDownloadDocument}
        sendComment={onSendDocumentComment}
        editComment={onEditComment}
        editCommentInProgress={editCommentInProgress}
        editCommentIsSuccess={editCommentIsSuccess}
        deleteComment={onDeleteComment}
        deleteCommentInProgress={deleteCommentInProgress}
        commentInProgress={commentInProgress}
        statusChangeInProgress={documentStatusChangeInProgress}
        changeCustomerDocumentsStatus={onChangeCustomerDocumentsStatus}
        onDownloadSuccess={downloadFileIsSuccess}
        getTransactionDocument={getTransactionDocumentyIdAction}
        documentIsLoading={documentIsLoading || documentIsFetching}
        activePermission={activePermissions}
        transactionInfo={incoming?.transaction}
        sendMessage={sendMessage}
        currenciesList={currenciesList}
        sidepanelExpandedKey="problem-incoming"
      />
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={incoming.blacklistDataVisible}
        onClose={handleCloseBlacklistData}
        title={incoming.blacklistData?.fullName as string}
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-incoming-blacklist"
      >
        <BlacklistDataContent data={incoming.blacklistData} />
      </SidePanel>
      <SidePanel
        type={SidePanelType.Transactions}
        closeOnClickOutside
        isOpen={incoming.blacklistItemVisibile}
        onClose={handleCloseBlacklistData}
        title="User Details"
        parent={
          document.getElementById('blacklist-data-sidepanel') as HTMLElement
        }
        footer={
          <ButtonPseudo text="Close" onClick={handleCloseBlacklistData} bold />
        }
        sidepanelExpandedKey="problem-incoming-blacklist"
        withoutExpand
        expanded
      >
        <BlacklistUserInfo info={incoming.blacklistItem} isLoading={false} />
      </SidePanel>
      <div id="blacklist-data-sidepanel" />
    </>
  );
};

export default ProblemIncomingTransactionHOC;
